// legal/DriverAgreement.js
// LoadDash Driver Independent Contractor Agreement
// Renders the full agreement as a scrollable document with signature capture.
// Original IP — LoadDash Services LLC

import React, { useState, useRef, useEffect } from "react";
import { supabase } from "../services/supabaseService";

const NAVY = "#0a1628";
const BLUE = "#3D90D9";
const GREEN = "#03A66A";
const RED = "#e53935";
const STEEL = "#607d9f";
const MUTED = "#8fa5be";
const BORDER = "#dce4ef";
const BG = "#f4f6f9";

const SECTIONS = [
  {
    title: "1. Who You Are",
    body: `You are an independent contractor, not an employee of LoadDash Services LLC. You control your own schedule, accept or decline any job, and operate your own vehicle. LoadDash is a technology platform that connects residents with independent operators. LoadDash does not direct how you perform your work.`,
  },
  {
    title: "2. Your Vehicle and Insurance",
    body: `You are responsible for maintaining valid auto insurance on your registered vehicle. You represent that your insurance covers use of your vehicle for independent contractor work. LoadDash is not responsible for damage to your vehicle or injuries arising from its use.`,
  },
  {
    title: "3. Weight Limits",
    body: `You agree that no single item you lift will exceed 50 pounds. This is a non-negotiable operating rule. Items over 50 pounds must be refused at pickup. If a customer disputes this, contact LoadDash support. Do not attempt to lift any item you cannot safely handle alone.`,
    critical: true,
  },
  {
    title: "4. Load Verification",
    body: `At each job you will independently assess the actual load and record your verified load size in the app. This verified value is your independent measurement. It is not influenced by the system-assigned classification. Your honest assessment protects you and the customer.`,
  },
  {
    title: "5. What LoadDash Covers",
    body: `LoadDash maintains commercial general liability insurance of $1,000,000 per occurrence and $2,000,000 aggregate covering platform operations. This does not cover your personal injury, your vehicle, or your actions as a contractor.`,
  },
  {
    title: "6. What You Cover",
    body: `You are responsible for your own safety, your own vehicle, any injuries you sustain, and any damage you cause. You agree to indemnify and hold harmless LoadDash Services LLC from any claims arising from your actions while performing services.`,
  },
  {
    title: "7. Market and Territory",
    body: `You are authorized to operate within Boyer Hill Military Housing at Hill Air Force Base, Utah. Operating in other markets requires separate authorization.`,
  },
  {
    title: "8. How This Ends",
    body: `Either party can end this agreement at any time. You can stop accepting jobs. LoadDash can remove your access. No notice period is required.`,
  },
  {
    title: "9. Governing Law",
    body: `This agreement is governed by the laws of the State of Utah.`,
  },
];

export default function DriverAgreement({ session, onComplete }) {
  const [fullName, setFullName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [error, setError] = useState("");
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const scrollRef = useRef(null);

  // Track scroll position to show bottom indicator
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function onScroll() {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
      if (atBottom) setScrolledToBottom(true);
    }
    el.addEventListener("scroll", onScroll);
    onScroll();
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  async function handleSign() {
    if (!fullName.trim() || !agreed) return;
    setSigning(true);
    setError("");
    try {
      const { error: rpcErr } = await supabase.rpc("record_driver_agreement", {
        full_name: fullName.trim(),
        agreed_at: new Date().toISOString(),
      });
      if (rpcErr) throw rpcErr;
      onComplete();
    } catch (e) {
      setError(e.message || "Failed to record agreement");
    }
    setSigning(false);
  }

  const canSign = fullName.trim().length >= 2 && agreed;

  return (
    <div style={{ minHeight: "100vh", background: BG, fontFamily: "'Sora',system-ui,sans-serif" }}>
      {/* Header */}
      <div style={{ background: `linear-gradient(160deg, #060e1a, ${NAVY})`, padding: "20px 18px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <img src="/Loaddashbutton.png" alt="LD" style={{ width: 28, height: 28, borderRadius: 7, objectFit: "contain" }} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", color: "#42a5f5" }}>LoadDash Haulers</span>
        </div>
        <h2 style={{ fontWeight: 900, fontSize: 20, color: "#fff", margin: 0, letterSpacing: -0.3 }}>Independent Contractor Agreement</h2>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,.35)", marginTop: 4 }}>Version ic-v1</div>
      </div>

      {/* Scrollable agreement body */}
      <div
        ref={scrollRef}
        style={{
          margin: "14px 18px 0", background: "#fff", borderRadius: 14,
          border: `1px solid ${BORDER}`, padding: "20px 18px",
          maxHeight: "50vh", overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <div style={{ fontSize: 14, color: NAVY, lineHeight: 1.7, fontWeight: 500, marginBottom: 16 }}>
          This Independent Contractor Agreement ("Agreement") is between you and LoadDash Services LLC ("LoadDash"). By signing below, you agree to all terms.
        </div>

        {SECTIONS.map((s, i) => (
          <div key={i} style={{ marginBottom: 20 }}>
            <div style={{
              fontWeight: 800, fontSize: 15, color: s.critical ? RED : NAVY,
              marginBottom: 6,
            }}>
              {s.title}
              {s.critical && (
                <span style={{
                  display: "inline-block", marginLeft: 8, fontSize: 9,
                  fontWeight: 700, padding: "2px 8px", borderRadius: 6,
                  background: "#fce4e4", color: RED, verticalAlign: "middle",
                  letterSpacing: 0.5, textTransform: "uppercase",
                }}>Critical</span>
              )}
            </div>
            <div style={{
              fontSize: 14, color: "#4a6080", lineHeight: 1.7,
              ...(s.critical ? { background: "#fff5f5", border: `1px solid #fca5a5`, borderRadius: 10, padding: "12px 14px" } : {}),
            }}>
              {s.body}
            </div>
          </div>
        ))}

        <div style={{ fontSize: 12, color: MUTED, textAlign: "center", padding: "8px 0", borderTop: `1px solid ${BORDER}` }}>
          End of agreement
        </div>
      </div>

      {/* Scroll indicator */}
      {!scrolledToBottom && (
        <div style={{ textAlign: "center", padding: "8px 0", fontSize: 11, color: BLUE, fontWeight: 600 }}>
          Scroll down to read the full agreement
        </div>
      )}

      {/* Signature area */}
      <div style={{ padding: "14px 18px 80px" }}>
        <div style={{
          background: "#fff", borderRadius: 14, border: `1px solid ${BORDER}`,
          padding: "20px 18px",
        }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: NAVY, marginBottom: 14 }}>Sign Agreement</div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: NAVY, marginBottom: 5 }}>Full Legal Name</div>
            <input
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              placeholder="First and last name as it appears on your ID"
              style={{
                width: "100%", background: BG, border: `1.5px solid ${BORDER}`,
                borderRadius: 10, color: NAVY, fontSize: 14, padding: "12px 14px",
                outline: "none", fontFamily: "inherit", boxSizing: "border-box",
              }}
            />
          </div>

          <div
            onClick={() => setAgreed(a => !a)}
            style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", marginBottom: 16 }}
          >
            <div style={{
              width: 22, height: 22, borderRadius: 6, flexShrink: 0, marginTop: 1,
              border: `2px solid ${agreed ? BLUE : BORDER}`,
              background: agreed ? BLUE : "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {agreed && <span style={{ color: "#fff", fontSize: 13, fontWeight: 700, lineHeight: 1 }}>v</span>}
            </div>
            <span style={{ fontSize: 13, color: NAVY, lineHeight: 1.5, fontWeight: 500 }}>
              I have read and agree to the LoadDash Driver Independent Contractor Agreement
            </span>
          </div>

          {error && (
            <div style={{
              background: "#fce4e4", border: "1px solid #ef9a9a", borderRadius: 10,
              padding: "10px 14px", fontSize: 13, color: RED, marginBottom: 14,
            }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSign}
            disabled={!canSign || signing}
            style={{
              width: "100%", padding: 15, borderRadius: 12, border: "none",
              background: canSign && !signing ? `linear-gradient(135deg, #1565c0, #1976d2)` : "#b0bec5",
              color: "#fff", fontWeight: 800, fontSize: 15,
              cursor: canSign && !signing ? "pointer" : "not-allowed",
              fontFamily: "inherit",
              boxShadow: canSign && !signing ? "0 4px 16px rgba(21,101,192,.35)" : "none",
            }}
          >
            {signing ? "Recording signature..." : "Sign Agreement"}
          </button>

          <div style={{ fontSize: 11, color: MUTED, textAlign: "center", marginTop: 12, lineHeight: 1.5 }}>
            Your signature will be recorded with a timestamp. This agreement is legally binding.
          </div>
        </div>
      </div>
    </div>
  );
}
